import React, { useState, useEffect, useCallback, FC } from "react";
import { format, subDays } from "date-fns";
import baseApi from "utils/axios-instance";
import { Mixpanel } from "utils/Analytics";
import { BarChart3, Clock, FileText, Loader2, RefreshCw } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Alert, AlertDescription } from "components/ui/alert";
import { Toaster } from "components/ui/sonner";
import { toast } from "sonner";
import DatePicker from "../RequestList/components/DatePicker";

interface DailyInsightsEntry {
  date: string;
  inquiries_submitted: number;
  time_saved_sec: number;
  avg_time_to_completion_sec: number;
}

interface InsightsData {
  from_date: string;
  to_date: string;
  daily: DailyInsightsEntry[];
  totals: {
    inquiries_submitted: number;
    time_saved_sec: number;
    avg_time_to_completion_sec: number;
  };
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0 min";
  if (seconds >= 86400) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }
  return `${Math.round(seconds / 60)} min`;
}

function formatTimeSaved(seconds: number): string {
  if (seconds <= 0) return "0 min";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const displayHours = hours + (minutes === 60 ? 1 : 0);
  const displayMinutes = minutes === 60 ? 0 : minutes;
  if (displayHours === 0) return `${displayMinutes} min`;
  if (displayMinutes === 0) return `${displayHours} hr`;
  return `${displayHours} hr ${displayMinutes} min`;
}

function formatChartDate(isoDate: string): string {
  return format(new Date(isoDate + "T00:00:00"), "MMM d");
}

function toApiDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

const InsightsPage: FC = () => {
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(subDays(today, 29));
  const [endDate, setEndDate] = useState<Date>(today);
  const [appliedStartDate, setAppliedStartDate] = useState<Date>(
    subDays(today, 29)
  );
  const [appliedEndDate, setAppliedEndDate] = useState<Date>(today);
  const [dateRangeError, setDateRangeError] = useState("");
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);

  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined
  ): boolean => {
    if (start && end && start > end) {
      setDateRangeError("Start date cannot be later than end date");
      return false;
    }
    setDateRangeError("");
    return true;
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    setStartDate(date);
    validateDateRange(date, endDate);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;
    setEndDate(date);
    validateDateRange(startDate, date);
  };

  const fetchInsights = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    try {
      const response = await baseApi.get("/dashboard/insights/data", {
        params: {
          from_date: toApiDate(from),
          to_date: toApiDate(to),
        },
        withCredentials: true,
      });
      setInsightsData(response.data);
    } catch (error: any) {
      Mixpanel.track("Error: Insights page load", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to load insights.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights(appliedStartDate, appliedEndDate);
  }, [appliedStartDate, appliedEndDate, fetchInsights]);

  const handleApplyDateRange = () => {
    if (!validateDateRange(startDate, endDate)) return;
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const applyPresetRange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    setDateRangeError("");
    setAppliedStartDate(start);
    setAppliedEndDate(end);
  };

  const handleLastWeek = () => {
    const end = new Date();
    applyPresetRange(subDays(end, 6), end);
  };

  const handleLastMonth = () => {
    const end = new Date();
    applyPresetRange(subDays(end, 29), end);
  };

  const chartData =
    insightsData?.daily.map((entry) => ({
      ...entry,
      label: formatChartDate(entry.date),
      time_saved_hours: entry.time_saved_sec / 3600,
      avg_completion_hours: entry.avg_time_to_completion_sec / 3600,
    })) ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <Toaster />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Insights</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Team-wide inquiry metrics aggregated by day
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleLastWeek} disabled={loading}>
            Last week
          </Button>
          <Button variant="outline" size="sm" onClick={handleLastMonth} disabled={loading}>
            Last month
          </Button>
          <DatePicker
            date={startDate}
            setDate={handleStartDateChange}
            placeholder="Start date"
            maxDate={endDate}
            error={!!dateRangeError}
          />
          <DatePicker
            date={endDate}
            setDate={handleEndDateChange}
            placeholder="End date"
            minDate={startDate}
            maxDate={today}
            error={!!dateRangeError}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleApplyDateRange}
            disabled={!!dateRangeError || loading}
          >
            Apply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchInsights(appliedStartDate, appliedEndDate)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {dateRangeError && (
        <Alert variant="destructive">
          <AlertDescription>{dateRangeError}</AlertDescription>
        </Alert>
      )}

      {loading && !insightsData ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-brand" />
        </div>
      ) : insightsData ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Inquiries submitted
                </CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {insightsData.totals.inquiries_submitted}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {format(new Date(insightsData.from_date + "T00:00:00"), "MMM d, yyyy")} –{" "}
                  {format(new Date(insightsData.to_date + "T00:00:00"), "MMM d, yyyy")}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Time saved
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatTimeSaved(insightsData.totals.time_saved_sec)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Total estimated staff time saved
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg. time to completion
                </CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(
                    insightsData.totals.avg_time_to_completion_sec
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Average across all inquiries in range (business days only)
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inquiries submitted per day</CardTitle>
              <CardDescription>
                Number of inquiries created each day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value: number) => [value, "Inquiries"]}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.date
                          ? format(
                              new Date(payload[0].payload.date + "T00:00:00"),
                              "MMM d, yyyy"
                            )
                          : ""
                      }
                    />
                    <Bar
                      dataKey="inquiries_submitted"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Time saved per day</CardTitle>
              <CardDescription>
                Staff time saved from inquiries created each day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatTimeSaved(value * 3600),
                        "Time saved",
                      ]}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.date
                          ? format(
                              new Date(payload[0].payload.date + "T00:00:00"),
                              "MMM d, yyyy"
                            )
                          : ""
                      }
                    />
                    <Bar
                      dataKey="time_saved_hours"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Average time to completion per day
              </CardTitle>
              <CardDescription>
                Average business-day time to completion for inquiries created each day (weekends and holidays excluded)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatDuration(value * 3600),
                        "Avg. completion time",
                      ]}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.date
                          ? format(
                              new Date(payload[0].payload.date + "T00:00:00"),
                              "MMM d, yyyy"
                            )
                          : ""
                      }
                    />
                    <Bar
                      dataKey="avg_completion_hours"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default InsightsPage;
